/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableModel} from '../../shared/table/models/table.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {BundleInformationTableColumnEnum} from './models/bundle-information-table-column.enum';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {BundlePositionsDetailModel} from '../models/bundle/bundle-positions-detail/bundle-positions-detail.model';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {BundleInformationTableStateModel} from './models/bundle-information-table.state.model';
import {SettlementDetailContract} from '../api/contracts/settlement/settlement-detail.contract';
import {BundleSpecDetailModel} from '../models/bundle/bundle-spec-detail/bundle-spec-detail.model';
import * as FinancialNumberHelper from '../util/financial-number/helper/financial-number-helper';
import bundleInformationTableHeaders from './configuration/bundle-information-table-headers-configuration';
import {BundleTypeEnum} from '../bundles-main-page/data/bundle-type.enum';
import {VariableColumnsService} from '../services/variable-columns/variable-columns.service';
import {SelectedTabsService} from '../services/selected-tabs.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from '../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModalBodyModel} from '../../shared/confirm-modal/confirm-modal/models/confirm-modal-body.model';
import {BundlesApi} from '../api/bundles.api';
import {Subscription} from 'rxjs';
import {BundleInformationTableBundleDataModel} from './models/bundle-information-table-bundle-data.model';
import {finalize} from 'rxjs/operators';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {BundleDetailModel} from '../models/bundle/bundle-detail/bundle-detail.model';
import {BundleClosedInformationModel} from '../period-bundles-table/models/bundle-closed-information.model';
import {UserBaseContract} from '../api/contracts/user-base.contract';
import {
    closedOnPipe,
    sentOnPipe
} from '../period-bundles-table/data-pipes/period-bundles-table-data-pipes';
import {PeriodBundlesTableStatusEnum} from '../period-bundles-table/models/period-bundles-table-status.enum';
import {ToastrService} from 'ngx-toastr';
import {SettlementDetailModel} from '../models/settlement/settlement-detail/settlement-detail.model';
import * as XLSX from 'xlsx';
import {SettlementPositionBaseContract} from '../api/contracts/settlement-position-base.contract';
import moment from 'moment';
import {BundlesConfigService} from '../services/bundles-config.service';
import {UserService} from '../../shared/services/user.service';
import {Permission} from '../../shared/utils/permission.enum';
import {BundleDetailAvailableActionsEnum} from '../api/contracts/bundle/bundle-detail-available-actions.enum';
import {BundleDetailMapper} from '../models/bundle/bundle-detail/bundle-detail.mapper';
import {BundleSentInformationModel} from '../period-bundles-table/models/bundle-sent-information.model';
import {InfoChartConfigurationModel} from '../../shared/info-chart/models/info-chart-configuration.model';
import {BundleInformationTableInfoChartDataMapper} from './models/mappers/bundle-information-table-info-chart-data.mapper';
import {PeriodDetailMapper} from '../models/period/period-detail/period-detail.mapper';
import {PeriodDetailModel} from '../models/period/period-detail/period-detail.model';
import {TableService} from '../../shared/table/table-service/table.service';
import tableLineConfiguration from '../../shared/table/table-line/configuration/table-line.configuration';
import {ResizeObserver} from 'resize-observer';
import {
    openLine,
    scrollIntoView
} from '../util/info-charts/info-chart';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {PeriodsTableSearchCriteriaService} from '../periods-table/services/periods-table-search-criteria.service';
import {PeriodResultContract} from '../api/contracts/period/period-result.contract';
import {PeriodResultMapper} from '../models/period/period-result/period-result.mapper';
import {PeriodQueryModel} from '../models/period/period-query/period-query.model';
import {PeriodQueryMapper} from '../models/period/period-query/period-query.mapper';
import {SwitchButtonConfiguration} from '../../shared/switch-button/configuration/switch-button.configuration';
import {SwitchButtonModel} from '../../shared/switch-button/models/switch-button.model';

type TEnum = BundleInformationTableColumnEnum | string;
type WorksheetData = any[][];

@Component({
    selector: 'app-bundle-information-table',
    templateUrl: './bundle-information-table.component.html',
    styleUrls: ['./bundle-information-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class BundleInformationTableComponent<Enum, DataObject> extends StatefulComponent<BundleInformationTableStateModel>
        implements OnDestroy, OnChanges, OnInit {
    @Input() bundlePositionsData: BundlePositionsDetailModel[];
    @Input() bundleCriteria: BundleSpecDetailModel;
    @Input() bundleData: BundleInformationTableBundleDataModel;
    @Input() bundleClosedInformation: BundleClosedInformationModel;
    @Input() bundleSentInformation: BundleSentInformationModel;
    @Input() timeZone: string;
    @Output() updatePeriodDetail = new EventEmitter<PeriodDetailModel>();
    @Output() updateBundleDetail = new EventEmitter<BundleDetailModel>();
    @Output() updateInfoChart = new EventEmitter<string>();

    public resizeObserver: ResizeObserver;
    public readonly EXPORT_BUNDLE_BUTTON_LABEL = 'Export EXCEL';
    public readonly BUNDLE_CRITERIA_TITLE = 'Bundle criteria';
    public readonly BUNDLE_CRITERIA_CYPRESS_TAG = 'bundle-criteria';
    public readonly BUNDLE_FLYING_MENU_TOOLTIP = 'View who marked/unmarked as sent and when';
    public readonly BUNDLE_MARKED_BY_LABEL = 'Last marked/unmarked by:';
    public readonly BUNDLE_MARKED_ON_LABEL = 'Marked/unmarked on:';
    sentSwitchButtonConfiguration: SwitchButtonConfiguration = {
        spaceInBetween: 28,
        labelOnRight: false,
        label: 'Mark as sent',
        tooltipText: 'Mark as sent after sharing this bundle file with your customer'
    };
    private readonly EXCEL_DATE_FORMAT: string = 'DD-MM-YYYY';
    private readonly bundleInfoChartDescription = 'Bundle total amount:';
    public tableData: TableModel<BundleInformationTableColumnEnum, SettlementDetailContract[]> = {
        headers: {
            entries: new Map<BundleInformationTableColumnEnum, TableHeaderEntryModel<BundleInformationTableColumnEnum>>([
                [BundleInformationTableColumnEnum.TOTAL_VALUE, bundleInformationTableHeaders.totalValue]
            ])
        },
        expandableLineTooltipDetail: this.getDynamicTooltip(),
        expandableLines: true
    };
    // Additional columns
    private criteriaSpecsCodes: string[] = [];
    private valuesSpecsCodes: string[] = [];
    private readonly ADDITIONAL_CRITERIA_SUFFIX = 'CRITERIA';
    private readonly ADDITIONAL_VALUE_SUFFIX = 'VALUE';

    private readonly BASE_ADDITIONAL_CRITERIA_INSERT_INDEX = 0;
    private readonly BASE_ADDITIONAL_VALUE_INSERT_INDEX = 1;
    // Close bundle modal variables
    private readonly CLOSE_BUNDLE_MODAL_TITLE = 'Close bundle confirmation';
    private readonly CLOSE_BUNDLE_MODAL_PRIMARY_BUTTON_LABEL = 'Close bundle';
    private readonly CLOSE_BUNDLE_MODAL_BODY_CONFIGURATION: ConfirmModalBodyModel = {
        width: 406,
        manualChecks: {
            title: 'Be aware that:',
            options: ['You will not be able to undo this action.']
        }
    };
    private readonly CLOSE_BUNDLE_NEW_DATA_MODAL_BODY_CONFIGURATION: ConfirmModalBodyModel = {
        width: 406,
        manualChecks: {
            title: 'Be aware that:',
            options: ['You will not be able to undo the close bundle action.']
        }
    };
    // Excel previous position values
    private readonly ADDITIONAL_CRITERIA_VALUE_INDEX_POSITION = 'Bundle close date';
    private readonly TOTAL_VALUE_INDEX_POSITION = 'Currency (ISO code)';
    private readonly BUNDLING_VALUE_INDEX_POSITION = 'Part description';
    private subscriptions = new Subscription();
    private readonly PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY = 'PERIOD_LIST_LOADING';
    private readonly BASE_WORKSHEET_HEADER: string[] = [
        'Bundle number',
        'Customer Code',
        'Customer Acronym',
        'Ship to Party Code',
        'Ship to Party Name',
        'Supplier code',
        'Supplier name',
        'Bundle type',
        'Bundle period',
        'Bundle close date',
        'Bundle total amount',
        'Currency (ISO code)',
        'Complaint number',
        'Settlement number',
        'Complaint creation date',
        'Customer complaint number',
        'Part number',
        'Part description',
        'Blue fapiao number',
        'Delivery number',
        'Sales document',
        'Tax_id'
    ];
    private readonly tooltipZebraMessage =
            'This bar represents the group of bundle aggregation lines that individually are too small to appear independently';
    private readonly paddingBox = 16;
    switchButtonModel: SwitchButtonModel;

    constructor(private selectedTabsService: SelectedTabsService,
                private variableColumns: VariableColumnsService,
                private modalService: NgbModal,
                private bundlesApi: BundlesApi,
                private spinnerService: SpinnerService,
                private bundlesConfigService: BundlesConfigService,
                private toast: ToastrService,
                private userService: UserService,
                private tableService: TableService<Enum, DataObject>,
                private host: ElementRef,
                private zone: NgZone,
                private periodsViewsService: PeriodsViewsService,
                private search: PeriodsTableSearchCriteriaService) {
        super();
    }

    /**
     * Returns an array of stings with the bundle criteria info to be display in the bundle criteria card
     */
    get bundleCriteriaInfo(): string[] {
        const finalCriteriaInfo: string[] = [];
        const maxNumberLabel = 'Maximum lines quantity: ';
        const maxAmountLabel = 'Maximum amount: ';
        const additionalCriteriaInfo = this.additionBundleCriteriaInfo;
        let maxNumber: string;
        let maxAmount: string;

        if (this.bundleCriteria == null) {
            maxNumber = maxNumberLabel + '-';
            maxAmount = maxAmountLabel + '-';
        } else {
            maxNumber = this.getMaxPositionsInfo(maxNumberLabel);
            maxAmount = this.getMaxAmountInfo(maxAmountLabel);
        }

        if (maxNumber != null) {
            finalCriteriaInfo.push(maxNumber);
        }
        if (maxAmount != null) {
            finalCriteriaInfo.push(maxAmount);
        }
        if (additionalCriteriaInfo != null) {
            finalCriteriaInfo.unshift(additionalCriteriaInfo);
        }

        return finalCriteriaInfo;
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): SettlementDetailModel[] | null {
        let accordionData: SettlementDetailModel[] | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    /**
     * Returns if the closed information should be visible
     */
    get showClosedInformation(): boolean {
        return this.bundleData?.status?.code === PeriodBundlesTableStatusEnum.CLOSED;
    }

    /**
     * Returns the user that closed the bundle
     */
    get bundleClosedBy(): UserBaseContract {
        return this.bundleClosedInformation?.closedBy;
    }

    /**
     * Returns the date when the bundle was closed
     */
    get bundleClosedOn(): string {
        return closedOnPipe(this.bundleClosedInformation?.closedOn);
    }

    /**
     * Returns if the bundle was sent
     */
    get bundleSent(): boolean {
        return this.bundleSentInformation?.sent;
    }

    /**
     * Returns the user that sent the bundle
     */
    get bundleSentBy(): UserBaseContract {
        return this.bundleSentInformation.sentBy;
    }

    /**
     * Returns the date when the bundle was sent
     */
    get bundleSentOn(): string {
        return sentOnPipe(this.bundleSentInformation?.sentOn);
    }

    /**
     * Returns the if the bundle was yet marked
     */
    get bundleIsMarked(): boolean {
        return this.bundleSentInformation.sentBy != null && this.bundleSentInformation.sentOn != null;
    }

    /**
     * Returns if the footer should be visible
     */
    get showFooter(): boolean {
        return this.bundleData?.availableActions?.includes(BundleDetailAvailableActionsEnum.CLOSE)
                && this.userService.hasPermissions([Permission.ADMIN_BUNDLE_CLOSE]);
    }

    /**
     * Returns if the sent and unsent features should be visible
     */
    get showSentUnsentAndFlyingMenu(): boolean {
        return (this.bundleData?.availableActions?.includes(
                        BundleDetailAvailableActionsEnum.SENT) || this.bundleData?.availableActions?.includes(
                        BundleDetailAvailableActionsEnum.UNSENT))
                && this.userService.hasPermissions([Permission.ADMIN_BUNDLE_SENT]);
    }

    /**
     * Returns if the header should be visible
     */
    get showHeader(): boolean {
        return this.showExcelButton || this.showSentUnsentAndFlyingMenu;
    }

    /**
     * Returns if the header should be visible
     */
    get showExcelButton(): boolean {
        return this.userService.hasPermissions([Permission.ADMIN_BUNDLE_EXPORT, Permission.CUSTOMER_BUNDLE_EXPORT])
                && this.bundleData?.status?.code === PeriodBundlesTableStatusEnum.CLOSED;
    }

    /*
     * Returns if the criteriaSpecs have length > 0
     */
    get hasBundleCriteriaSpecs(): boolean {
        return this.bundleCriteria?.criteriaSpecs?.length > 0;
    }

    get hasBundlePositionCriteria(): boolean {
        if (this.bundlePositionsData != null) {
            return this.bundlePositionsData[0]?.criteria?.length > 0;
        }
    }

    get totalBundlesPositionsAmount(): number {
        return this.bundlePositionsData?.map((item) => item.totalValue)
                .reduce((totalAmount, current) => totalAmount + current, 0) ?? 0;
    }

    ngOnInit(): void {
        this.switchButtonModel = {
            checked: this.bundleSent,
            disabled: false
        };
        this.updatePeriodTypeInfoChartLineHeight();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['bundlePositionsData']) {
            // Must be run before retrieveTableData() because all the headers must be in place before mapping the table data
            this.addAdditionalColumnHeaders();
            this.updateTotalAmountHeaderConfiguration();
            this.retrieveTableData();
            this.tableData.expandableLineTooltipDetail = this.getDynamicTooltip();
        }
    }

    ngOnDestroy(): void {
        this.tableService
                .setOpenLineHeightValue({identifier: this.bundleData?.bundleNumber, height: tableLineConfiguration.tableLineHeight});
        this.resizeObserver.unobserve(this.host.nativeElement);
        this.subscriptions.unsubscribe();
    }

    mouseHoverHandler(tableLine: TableLineModel<BundleInformationTableColumnEnum, PeriodDetailModel>): void {
        this.storeState({mouseHoverLineIdentifier: tableLine?.identifier});
    }

    mouseHoverLine(identifier: number): void {
        this.tableData.staticSection?.map((item) => item.isMouseHover = false);
        if (identifier != null) {
            const line = this.tableData.staticSection?.find((item) => item.identifier === identifier.toString());
            if (line != null) {
                line.isMouseHover = !line.isMouseHover;
            }
        }
    }

    openLine(identifier: string,
             isFromChart?: boolean,
             hasScroll?: boolean): void {
        const retrievedLine = openLine<BundleInformationTableColumnEnum, SettlementDetailContract[]>(
                this.tableData,
                identifier,
                isFromChart);
        if (retrievedLine != null) {
            retrievedLine.accordionData = this.bundlePositionsData?.find((bundleInfo) =>
                    bundleInfo.position?.toString() === retrievedLine?.identifier)?.settlementsList;

            if (hasScroll) {
                scrollIntoView(retrievedLine.identifier);
            }

            this.storeState({selectedLine: retrievedLine});
        }

    }

    /**
     * Returns the tooltip for the Table lines and Info Chart bar's
     * @param prefix option to add a prefix to the dynamically generated tooltip. If not passed doesn't add a prefix.
     * @returns the dynamically generated tooltip
     */
    getDynamicTooltip(): string {
        let tooltip;
        const [firstPosition] = this.bundlePositionsData ?? [];
        const criteriaSpecsLength = firstPosition?.spec?.criteriaSpecs?.length;

        if (criteriaSpecsLength === 0) {
            tooltip = `${this.retrieveColumnNameTableData(this.selectedTabsService.bundleType)} total amount line`;
        } else if (criteriaSpecsLength === 1) {
            const bundleType = firstPosition.spec.criteriaSpecs[0].name;
            tooltip = `${bundleType} line`;
        } else {
            tooltip = `bundle aggregation line`;
        }
        return tooltip;
    }

    bundleTotalAmountInfoChart(): InfoChartConfigurationModel<number> {
        return {
            description: this.bundleInfoChartDescription,
            totalValue: this.totalBundlesPositionsAmount,
            interactable: true,
            infoData: BundleInformationTableInfoChartDataMapper
                    .fromSettlementPositionBaseContractToInfoChartDataModelArray(
                            this.bundlePositionsData, this.selectedTabsService.bundleType),
            tooltip: this.getDynamicTooltip(),
            currency: this.bundleCriteria?.currency,
            zebraTooltip: this.tooltipZebraMessage
        };
    }

    /**
     * Calls the BE to check if the bundle was changes and opens the correct close bundle confirmation modal
     */
    closeBundleClick(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundlesApi.validateBundle(this.bundleData?.bundleNumber, this.bundleData?.metadata)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((validationResult) => {
                            if (validationResult.isValid) {
                                this.storeState({identifier: this.bundleData?.metadata});
                                this.openCloseBundleUnchangedDataModal();
                            } else {
                                this.storeState({identifier: validationResult.actual.metadata});
                                this.openCloseBundleChangedDataModal(
                                        FinancialNumberHelper.formatWithCurrencySymbol(validationResult.actual.totalValue,
                                                this.bundleCriteria.currency.alphabeticCode));
                            }
                        }));
    }

    /**
     * Exports the bundle report excel
     */
    exportExcel(): void {
        const workbook = XLSX.utils.book_new();
        const worksheetHeader = this.retrieveFinalWorksheetHeader();
        const worksheet = XLSX.utils.json_to_sheet(this.mapWorksheetData(worksheetHeader), {skipHeader: true});

        // Format the date/time columns, so they don't show up as ######
        worksheet['!cols'] = new Array(worksheetHeader.length);
        worksheet['!cols'][worksheetHeader.indexOf('Bundle close date')] = {wch: 10.5};
        worksheet['!cols'][worksheetHeader.indexOf('Complaint creation date')] = {wch: 10.5};

        XLSX.utils.book_append_sheet(workbook, worksheet, `Bundle ${this.bundleData.bundleNumber} report`);

        XLSX.writeFile(workbook, `BUNDLE_${this.bundleData.bundleNumber}.xlsx`, {cellDates: true});
    }

    /**
     * Toggles the bundle sent/unsent action
     */
    toggleSent(sentAction: boolean): void {
        return sentAction ? this.markAsSent(this.bundleData.bundleNumber) : this.markAsUnsent(this.bundleData.bundleNumber);
    }

    private markAsSent(bundleNumber: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundlesApi.markBundleAsSent(bundleNumber)
                        .subscribe(() => {
                            this.toast.success(
                                    `The bundle ${bundleNumber} was successfully marked as sent`,
                                    'Success');
                            this.refreshBundleDetail(bundleNumber);
                        })
                        .add(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    private markAsUnsent(bundleNumber: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundlesApi.markBundleAsUnsent(bundleNumber)
                        .subscribe(() => {
                            this.toast.success(
                                    `The bundle ${bundleNumber} was successfully marked as unsent`,
                                    'Success');
                            this.refreshBundleDetail(bundleNumber);
                        })
                        .add(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        })
        );
    }

    private retrieveColumnNameTableData(bundleType: BundleTypeEnum): string {
        let label = '';
        switch (bundleType) {
            case BundleTypeEnum.PARTS_BUNDLING:
                label = 'Parts';
                break;
            case BundleTypeEnum.TE_MINUTES_BUNDLING:
                label = 'TE minutes';
                break;
        }
        return label;
    }

    /**
     * Returns the total amount header correct title, according to the bundle type or the aggregation criteria
     */
    private getTotalAmountColumnTitle(): string {
        let title;
        if (this.hasBundlePositionCriteria) {
            title = 'Total amount';
        } else {
            const bundleType = this.retrieveColumnNameTableData(this.selectedTabsService.bundleType);
            title = `${bundleType} total amount`;
        }
        return title;
    }

    private retrieveTableData(): void {
        this.tableData.staticSection = this.mapData(this.bundlePositionsData);
    }

    private mapData(bundles: BundlePositionsDetailModel[]):
            TableLineModel<BundleInformationTableColumnEnum, SettlementDetailContract[]>[] {
        let lines: TableLineModel<BundleInformationTableColumnEnum, SettlementDetailContract[]>[] = [];

        if (bundles != null) {
            lines = bundles.map((bundle) => ({
                isOpen: false,
                data: this.mapLineData(bundle),
                identifier: `${bundle.position}`
            } as TableLineModel<BundleInformationTableColumnEnum, SettlementDetailContract[]>));
        }
        return lines;
    }

    private mapLineData(data: BundlePositionsDetailModel): Map<BundleInformationTableColumnEnum, TableLineDataModel> {
        const map = new Map<BundleInformationTableColumnEnum, TableLineDataModel>([
            [
                BundleInformationTableColumnEnum.TOTAL_VALUE, {
                cellValue: {
                    data: data.totalValue,
                    currency: this.bundleCriteria?.currency
                },
                copyPasteValue: data.totalValue
            }
            ]
        ]);
        this.addAdditionalColumnValues(map, data);
        return map;
    }

    /**
     * Updated the total amount header so that the header label matches the bundle positions additional information
     */
    private updateTotalAmountHeaderConfiguration(): void {
        this.tableData.headers.entries.set(BundleInformationTableColumnEnum.TOTAL_VALUE, {
            ...this.tableData.headers.entries.get(BundleInformationTableColumnEnum.TOTAL_VALUE),
            name: this.getTotalAmountColumnTitle()
        });
    }

    /**
     * Adds the additional spec and value headers to the table
     */
    private addAdditionalColumnHeaders(): void {
        // Add additional spec column headers
        this.variableColumns.addVariableColumnsHeaders(
                this.tableData.headers.entries,
                this.variableColumns.getCriteriaSpecs(this.bundlePositionsData),
                this.criteriaSpecsCodes,
                bundleInformationTableHeaders.additionalCriterion,
                this.BASE_ADDITIONAL_CRITERIA_INSERT_INDEX,
                this.ADDITIONAL_CRITERIA_SUFFIX);
        // Add additional values column headers
        this.variableColumns.addVariableColumnsHeaders(
                this.tableData.headers.entries,
                this.variableColumns.getValuesSpecs(this.bundlePositionsData),
                this.valuesSpecsCodes,
                bundleInformationTableHeaders.additionalValue,
                this.BASE_ADDITIONAL_VALUE_INSERT_INDEX,
                this.ADDITIONAL_VALUE_SUFFIX);
    }

    /**
     * Adds the additional spec and value values to a table line
     * @param tableLine the table line into which the values must be added
     * @param data the {@link BundleDetailModel} containing the line value info
     */
    private addAdditionalColumnValues(tableLine: Map<TEnum, TableLineDataModel>, data: BundlePositionsDetailModel): void {
        // Add additional spec column values
        this.variableColumns.addVariableColumnsValues(tableLine, this.criteriaSpecsCodes, this.BASE_ADDITIONAL_CRITERIA_INSERT_INDEX,
                data.criteria, this.ADDITIONAL_CRITERIA_SUFFIX);
        // Add additional values column values
        this.variableColumns.addVariableColumnsValues(tableLine, this.valuesSpecsCodes, this.BASE_ADDITIONAL_VALUE_INSERT_INDEX,
                data.values, this.ADDITIONAL_VALUE_SUFFIX, this.bundleCriteria.currency);
    }

    /**
     * Opens the unchanged data close bundle confirmation modal
     */
    private openCloseBundleUnchangedDataModal(): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        confirmModalInstance.modalConfiguration = {
            title: this.CLOSE_BUNDLE_MODAL_TITLE,
            primaryActionTitle: this.CLOSE_BUNDLE_MODAL_PRIMARY_BUTTON_LABEL,
            secondaryActionTitle: 'Discard',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            body: this.CLOSE_BUNDLE_MODAL_BODY_CONFIGURATION
        };
        confirmModalInstance.primaryEvent.subscribe(() => this.closeBundle());
    }

    /**
     * Opens the changed data close bundle confirmation modal
     * @param newTotalAmount the new bundle total amount
     */
    private openCloseBundleChangedDataModal(newTotalAmount: string): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        confirmModalInstance.modalConfiguration = {
            title: this.CLOSE_BUNDLE_MODAL_TITLE,
            primaryActionTitle: this.CLOSE_BUNDLE_MODAL_PRIMARY_BUTTON_LABEL,
            secondaryActionTitle: 'Discard and refresh',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            body: {
                ...this.CLOSE_BUNDLE_NEW_DATA_MODAL_BODY_CONFIGURATION,
                subtitle: [
                    `The bundle total amount has changed since you last opened it. The new total amount is ${newTotalAmount}.`,
                    'Do you wish to close it or discard and refresh the bundle’s data?'
                ]
            }
        };
        confirmModalInstance.primaryEvent.subscribe(() => this.closeBundle());
        confirmModalInstance.secondaryEvent.subscribe(() => {
            this.refreshBundleDetail(this.bundleData.bundleNumber);
        });
    }

    /**
     * BE request to close the bundle
     */
    private closeBundle(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundlesApi.closeBundle(this.bundleData?.bundleNumber, this.state?.identifier)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe(
                                (success) => {
                                    this.toast.success(`The bundle ${this.bundleData?.bundleNumber} was successfully closed!`, 'Success');
                                })
                        .add(() => this.refreshBundleDetail(this.bundleData?.bundleNumber)));
    }

    /**
     * Retrieves the updated bundle detail and triggers the action that updates the view.
     * Calls updateBundleDetail.emit() with the new bundle detail to trigger the period-bundles-table selected line update
     */
    private refreshBundleDetail(bundleIdentifier: string): void {
        this.periodsViewsService.updateTableLinesByGlobalSearchButton = false;
        if (this.periodsViewsService.isGlobalSearchCriteriaApplied()) {
            this.refreshBundleDetailWithPeriodDetailWithGlobalSearchCriteria(bundleIdentifier);
        } else {
            this.refreshBundleDetailWithPeriodDetail(bundleIdentifier);
        }
    }

    private refreshBundleDetailWithPeriodDetailWithGlobalSearchCriteria(bundleIdentifier: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);

        const periodQuery: PeriodQueryModel = {
            ...this.search.currentPeriodQuery,
            searchCriteria: {
                ...this.search.currentPeriodQuery.searchCriteria,
                periodCode: this.bundleData?.periodCode
            }
        };

        this.subscriptions.add(
                this.bundlesApi.getPeriods(PeriodQueryMapper.toPeriodQueryContract(periodQuery))
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((result: PeriodResultContract) => {
                            const periodsResult = PeriodResultMapper.fromPeriodResultContract(result,
                                    this.bundlesConfigService.timezone);
                            const periodDetail = periodsResult.periodsPage?.pop();

                            this.updateInfoChart.emit(bundleIdentifier);
                            this.updatePeriodDetail.emit(periodDetail);
                            const bundleDetail = periodDetail.bundlesList.find((item) => item.number === bundleIdentifier);
                            this.updateBundleDetail.emit(BundleDetailMapper.fromBundleDetailContract(bundleDetail));
                        })
                        .add(() => {
                            this.updatePeriodTypeInfoChartLineHeightOnRefreshBundleDetail();
                        }));
    }

    private refreshBundleDetailWithPeriodDetail(bundleIdentifier: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.bundlesApi.getPeriodDetail(this.bundleData?.periodCode)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.PERIOD_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((periodDetail) => {
                            this.updateInfoChart.emit(bundleIdentifier);
                            this.updatePeriodDetail.emit(PeriodDetailMapper.fromPeriodDetailContract(periodDetail));
                            const bundleDetail = periodDetail.bundlesList.find((item) => item.number === bundleIdentifier);
                            this.updateBundleDetail.emit(BundleDetailMapper.fromBundleDetailContract(bundleDetail));
                        })
                        .add(() => {
                            this.updatePeriodTypeInfoChartLineHeightOnRefreshBundleDetail();
                        }));
    }

    private getWorksheetEntriesPositionIndex(worksheetHeader: string[], previousPositionValue: string): number {
        if (worksheetHeader != null && previousPositionValue != null) {
            return worksheetHeader?.indexOf(previousPositionValue) + 1;
        }
    }

    /**
     * Returns the complete worksheet data to create the bundle excel report
     */
    private mapWorksheetData(worksheetHeader: string[]): WorksheetData {
        const worksheetData: WorksheetData = [];

        this.bundlePositionsData?.forEach((position) => {
            position.settlementsList?.forEach((settlement) => {
                settlement.settlementPositions?.filter((item) => item != null
                        && (this.selectedTabsService.bundleType === BundleTypeEnum.PARTS_BUNDLING && item.materialsTotalGrossCost > 0
                                || this.selectedTabsService.bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING &&
                                item.teMinutesTotalCost > 0))
                        .forEach((settlementPosition) => {
                            // Fixed criteria data
                            const line: (string | number)[] = this.retrieveWorksheetFixedCriteriaData(settlement, settlementPosition);

                            // Additional criteria
                            this.addWorksheetAdditionalCriteriaData(worksheetHeader, line);

                            // Bundling type dependent data
                            this.addWorksheetBundlingTypeDependentData(worksheetHeader, line, position, settlement, settlementPosition);

                            worksheetData.push(line);
                        });
            });
        });

        return [worksheetHeader].concat(worksheetData);
    }

    /**
     * Return an array with the fixed values for a worksheet line
     * @param settlement the {@link SettlementDetailModel} with the data to be inserted into the line
     * @param settlementPosition the {@link SettlementPositionBaseContract} with the data to be inserted into the line
     */
    private retrieveWorksheetFixedCriteriaData(settlement: SettlementDetailModel,
                                               settlementPosition: SettlementPositionBaseContract): any[] {
        return [
            this.bundleData.bundleNumber,
            this.selectedTabsService.customerCode,
            this.selectedTabsService.customerAcronym,
            settlement.shipToParty.code,
            settlement.shipToParty.name,
            settlement.supplier.code,
            settlement.supplier.name,
            this.selectedTabsService.bundleType,
            `${moment(this.bundleData.timePeriod.startDate)
                    .tz(this.bundlesConfigService.timezone)
                    ?.format(this.EXCEL_DATE_FORMAT)} - ${moment(this.bundleData.timePeriod.endDate)
                    .tz(this.bundlesConfigService.timezone)
                    ?.format(this.EXCEL_DATE_FORMAT)}`,
            `${moment(this.bundleData.bundleCloseDate)
                    .tz(this.bundlesConfigService.timezone)
                    ?.format(this.EXCEL_DATE_FORMAT)}`,
            this.bundleData.bundleTotalAmount,
            settlement.currency.alphabeticCode,
            settlement.complaintCode,
            settlement.settlementCode,
            `${moment(settlement.claimCreationDate)
                    .tz(this.bundlesConfigService.timezone)
                    ?.format(this.EXCEL_DATE_FORMAT)}`,
            settlement.customerComplaintNumber,
            settlement.material.number,
            settlement.material.description,
            settlementPosition.blueFapiaoNumber,
            settlementPosition.deliveryNumber,
            settlementPosition.salesDocument,
            settlement.taxId
        ];
    }

    /**
     * Given the worksheet header and a line, inserts the additional criteria data into the line correct positions
     * @param worksheetHeader the complete worksheet header array
     * @param line the line array into which the additional criteria should be inserted
     */
    private addWorksheetAdditionalCriteriaData(worksheetHeader: string[], line: (string | number)[]): void {
        const additionalCriteriaIndex = this.getWorksheetEntriesPositionIndex(
                worksheetHeader, this.ADDITIONAL_CRITERIA_VALUE_INDEX_POSITION);

        // Additional criteria data
        const additionalCriteriaData = this.bundleData?.bundleAdditionalCriteria?.map((criteria) => criteria.value)
                ?.reverse();
        additionalCriteriaData?.forEach((spec) => line.splice(additionalCriteriaIndex, 0, spec));
    }

    /**
     * Given the worksheet header and a line, inserts the bundle dependent data into the line correct positions
     * @param worksheetHeader the complete worksheet header array
     * @param line the line array into which the additional criteria should be inserted
     * @param bundlePosition the {@link BundlePositionsDetailModel} to which the worksheet line corresponds
     * @param settlement the {@link SettlementDetailModel} to which the worksheet line corresponds
     * @param settlementPosition the {@link SettlementPositionBaseContract} to which the worksheet line corresponds
     */
    private addWorksheetBundlingTypeDependentData(worksheetHeader: string[],
                                                  line: (string | number)[],
                                                  bundlePosition: BundlePositionsDetailModel,
                                                  settlement: SettlementDetailModel,
                                                  settlementPosition: SettlementPositionBaseContract): void {
        const totalAmountIndex = this.getWorksheetEntriesPositionIndex(worksheetHeader, this.TOTAL_VALUE_INDEX_POSITION);
        // TE minutes bundling
        if (this.selectedTabsService?.bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING) {
            if (this.hasBundlePositionCriteria) {
                line.splice(totalAmountIndex, 0, this.bundleData.bundleTotalAmount);
            }
            const bundlingDependentHeadersIndex =
                    this.getWorksheetEntriesPositionIndex(worksheetHeader, this.BUNDLING_VALUE_INDEX_POSITION);
            // Data
            line.splice(bundlingDependentHeadersIndex, 0,
                    settlementPosition.teMinutesAmount,
                    settlementPosition.teMinutesUnitCost,
                    settlementPosition.teMinutesAdditionalCost,
                    settlementPosition.teMinutesTotalCost
            );

            // Parts bundling
        } else if (this.selectedTabsService.bundleType === BundleTypeEnum.PARTS_BUNDLING) {
            if (this.hasBundlePositionCriteria) {
                line.splice(totalAmountIndex, 0, bundlePosition.totalValue);

                // Data
                line.splice(totalAmountIndex, 0,
                        bundlePosition.criteria.find((item) => item).value);
            }

            const bundlingDependentHeadersIndex = this.getWorksheetEntriesPositionIndex(
                    worksheetHeader, this.BUNDLING_VALUE_INDEX_POSITION);
            // Data
            line.splice(bundlingDependentHeadersIndex, 0,
                    settlementPosition.quantity,
                    settlementPosition.materialsUnitNetCost,
                    settlementPosition.materialsTotalNetCost,
                    settlementPosition.taxRate,
                    settlementPosition.materialsTotalGrossCost
            );
        }
    }

    /**
     * Returns the complete worksheet header line according to the bundle configuration
     */
    private retrieveFinalWorksheetHeader(): string[] {
        const finalWorksheetHeader = [...this.BASE_WORKSHEET_HEADER];
        // Additional criteria headers
        const additionalCriteriaIndex = this.getWorksheetEntriesPositionIndex(
                finalWorksheetHeader, this.ADDITIONAL_CRITERIA_VALUE_INDEX_POSITION);
        const additionalCriteriaHeaders = this.bundleCriteria?.criteriaSpecs?.map((criteria) => criteria.name)
                ?.reverse();
        additionalCriteriaHeaders?.forEach((spec) => finalWorksheetHeader.splice(additionalCriteriaIndex, 0, spec));
        // Bundling type dependent headers
        const totalAmountIndex = this.getWorksheetEntriesPositionIndex(finalWorksheetHeader, this.TOTAL_VALUE_INDEX_POSITION);
        const bundlingDependentHeadersIndex = this.getWorksheetEntriesPositionIndex(
                finalWorksheetHeader, this.BUNDLING_VALUE_INDEX_POSITION);

        // TE minutes bundling headers
        if (this.selectedTabsService?.bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING) {
            finalWorksheetHeader.splice(bundlingDependentHeadersIndex, 0,
                    'TE minutes quantity',
                    'TE minutes price per minute',
                    'TE minutes additional costs',
                    'TE minutes total amount'
            );

            // Parts bundling headers
        } else if (this.selectedTabsService?.bundleType === BundleTypeEnum.PARTS_BUNDLING) {
            finalWorksheetHeader.splice(bundlingDependentHeadersIndex, 0,
                    'Parts quantity',
                    'Parts net price',
                    'Parts total net price',
                    'Tax rate',
                    'Parts total amount'
            );

            // Aggregation type dependent headers
            if (this.hasBundlePositionCriteria) {
                finalWorksheetHeader.splice(totalAmountIndex, 0, 'Bundle line total amount');
                const [firstPosition] = this.bundlePositionsData ?? [];
                firstPosition?.spec.criteriaSpecs.forEach((item, index) => {
                    finalWorksheetHeader.splice(totalAmountIndex + index, 0, item.name);
                });
            }
        }
        return finalWorksheetHeader;
    }

    private updatePeriodTypeInfoChartLineHeightOnRefreshBundleDetail(): void {
        this.resizeObserver.unobserve(this.host.nativeElement);
        this.updatePeriodTypeInfoChartLineHeight();
    }

    private updatePeriodTypeInfoChartLineHeight(): void {
        this.resizeObserver = new ResizeObserver((entries) => {
            this.zone.run(() => {
                this.tableService.setOpenLineHeightValue({
                    identifier: this.bundleData?.bundleNumber,
                    height: entries[0].contentRect.height + tableLineConfiguration.tableLineHeight + this.paddingBox
                });
            });
        });
        this.resizeObserver.observe(this.host.nativeElement);
    }

    /**
     * Returns a string with the additional bundle criteria info to be display in the bundle criteria card
     */
    private get additionBundleCriteriaInfo(): string {
        let additionBundleCriteria: string = null;
        const criteriaSpecs = this.bundleCriteria?.criteriaSpecs;

        if (this.hasBundleCriteriaSpecs) {
            additionBundleCriteria = 'Grouped by: ';

            criteriaSpecs.forEach((spec, index) => {
                const criteriaSpec = spec.name.toLowerCase();

                if (index === criteriaSpecs.length - 1) {
                    additionBundleCriteria += criteriaSpec;
                } else {
                    additionBundleCriteria += `${criteriaSpec}, `;
                }
            });
        }
        return additionBundleCriteria;
    }

    private getMaxPositionsInfo(label: string): string {
        if (this.bundleCriteria.maxPositions != null) {
            return label + `${this.bundleCriteria?.maxPositions}`;
        }
    }

    private getMaxAmountInfo(label: string): string {
        let infoText = label;
        if (this.bundleCriteria.maxValue != null) {
            infoText += `${FinancialNumberHelper
                .formatWithCurrencySymbol(this.bundleCriteria?.maxValue, this.bundleCriteria?.currency?.alphabeticCode)}`;
        } else {
            infoText += 'Unlimited';
        }
        return infoText;
    }
}
